import { useState } from 'react';
import {
    BookOpen,
    Target,
    Compass,
    CheckCircle,
    ArrowRight,
    Bell,
    Clock,
    Zap,
    Mail,
    Calendar
} from 'lucide-react';
const Main = () => {
    const companyProfile = {
        name: "InnovateAI Ltd",
        analysisScores: {
            trademark: 85,
            domain: 92,
            disputes: 78,
            valuation: 88
        },
        characteristics: {
            size: "Mid-Market",
            growth: "High",
            ipAwareness: "Medium",
            budget: "Substantial",
            decisionCycle: "Quick"
        }
    };

    // Playbook strategies based on company characteristics
    const playbooks = {
        highGrowth: {
            name: "High-Growth IP Strategy",
            score: 92,
            suitability: "Perfect match",
            timeline: "3-6 months",
            approach: [
                {
                    stage: "Initial Contact",
                    action: "Executive IP Review Meeting",
                    timing: "Week 1",
                    priority: "high"
                },
                {
                    stage: "Portfolio Assessment",
                    action: "Comprehensive IP Audit",
                    timing: "Week 2-3",
                    priority: "high"
                },
                {
                    stage: "Strategy Development",
                    action: "Growth-Focused IP Roadmap",
                    timing: "Week 4",
                    priority: "medium"
                },
                {
                    stage: "Implementation",
                    action: "Phased Protection Rollout",
                    timing: "Month 2-6",
                    priority: "medium"
                }
            ],
            keyBenefits: [
                "Rapid portfolio expansion",
                "Strategic market protection",
                "Competitive advantage securing",
                "Investment readiness"
            ]
        },
        ipOptimization: {
            name: "IP Portfolio Optimization",
            score: 85,
            suitability: "Strong match",
            timeline: "2-4 months",
            approach: [
                {
                    stage: "Portfolio Review",
                    action: "Gap Analysis",
                    timing: "Week 1",
                    priority: "high"
                },
                {
                    stage: "Strategy Alignment",
                    action: "Business Goals Workshop",
                    timing: "Week 2",
                    priority: "medium"
                },
                {
                    stage: "Optimization Plan",
                    action: "Cost-Benefit Analysis",
                    timing: "Week 3-4",
                    priority: "medium"
                }
            ],
            keyBenefits: [
                "Cost optimization",
                "Portfolio streamlining",
                "Strategic alignment",
                "Efficiency gains"
            ]
        }
    };

    // Communication templates
    const templates = {
        executive: {
            subject: "Strategic IP Partnership Opportunity",
            approach: "Value-focused, ROI-driven discussion"
        },
        technical: {
            subject: "IP Portfolio Optimization Proposal",
            approach: "Detailed technical analysis and recommendations"
        }
    };
    const getBadgeColor = (priority: string) => {
        switch (priority) {
            case "high":
                return "bg-[#fee2e2] text-[#991b1b]";
            case "medium":
                return "bg-[#fef9c3] text-[#854d0e]";
            default:
                return "bg-[#dbeafe] text-[#1e40af]";
        }
    };
    return (
        <div className="space-y-6">
            {/* Recommended Playbook */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5" />
                        <span className='font-bold'>Recommended Playbook</span>
                    </div>
                    <div>
                        AI-generated approach based on company analysis
                    </div>
                </div>
                <div>
                    <div className="space-y-6 mt-2">
                        {/* Company Profile Summary */}
                        <div className="p-4 bg-gray-50 rounded-lg">
                            <h3 className="font-medium mb-2">Company Profile</h3>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                <div>
                                    <span className="text-sm text-gray-500">Size</span>
                                    <div className="font-medium">{companyProfile.characteristics.size}</div>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">Growth</span>
                                    <div className="font-medium">{companyProfile.characteristics.growth}</div>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">IP Awareness</span>
                                    <div className="font-medium">{companyProfile.characteristics.ipAwareness}</div>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">Budget</span>
                                    <div className="font-medium">{companyProfile.characteristics.budget}</div>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">Decision Cycle</span>
                                    <div className="font-medium">{companyProfile.characteristics.decisionCycle}</div>
                                </div>
                            </div>
                        </div>

                        {/* Primary Recommended Playbook */}
                        <div className="border rounded-lg">
                            <div className="p-4 border-b">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <h3 className="font-medium">{playbooks.highGrowth.name}</h3>
                                        <p className="text-sm text-gray-500">{playbooks.highGrowth.suitability}</p>
                                    </div>
                                    <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-[#d1fae5] text-[#065f46]">
                                        {playbooks.highGrowth.score}% Match
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Clock className="w-4 h-4" />
                                    Timeline: {playbooks.highGrowth.timeline}
                                </div>
                            </div>

                            {/* Approach Steps */}
                            <div className="p-4 space-y-4">
                                {playbooks.highGrowth.approach.map((step, index) => (
                                    <div key={index} className="flex items-start gap-4">
                                        <div className="flex-shrink-0 w-24 text-sm text-gray-500">
                                            {step.timing}
                                        </div>
                                        <div className="flex-grow">
                                            <div className="flex items-center gap-2">
                                                <div className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm ${getBadgeColor(step.priority)}`}>
                                                    {step.stage}
                                                </div>
                                            </div>
                                            <p className="text-sm mt-1">{step.action}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Key Benefits */}
                            <div className="p-4 border-t bg-gray-50">
                                <h4 className="font-medium mb-2">Key Benefits</h4>
                                <div className="grid grid-cols-2 gap-2">
                                    {playbooks.highGrowth.keyBenefits.map((benefit, index) => (
                                        <div key={index} className="flex items-center gap-2 text-sm">
                                            <CheckCircle className="w-4 h-4 text-green-500" />
                                            {benefit}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-4">
                            <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                <Mail className="w-4 h-4" />
                                Generate Introduction
                            </button>
                            <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">   
                            <Calendar className="w-4 h-4" />
                                Schedule Strategy Call
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Communication Strategy */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        <span className='font-bold'>Communication Strategy</span>
                    </div>
                </div>
                <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                        <div className="p-4 border rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                                <Bell className="w-4 h-4" />
                                <h3 className="font-medium">Recommended Timing</h3>
                            </div>
                            <div className="space-y-2">
                                <div className="text-sm flex justify-between">
                                    <span>Best Day</span>
                                    <span className="font-medium">Tuesday/Thursday</span>
                                </div>
                                <div className="text-sm flex justify-between">
                                    <span>Best Time</span>
                                    <span className="font-medium">10:00-11:30 AM</span>
                                </div>
                                <div className="text-sm flex justify-between">
                                    <span>Follow-up</span>
                                    <span className="font-medium">+2 Days</span>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 border rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                                <Zap className="w-4 h-4" />
                                <h3 className="font-medium">Key Talking Points</h3>
                            </div>
                            <div className="space-y-2">
                                <div className="text-sm flex items-center gap-2">
                                    <ArrowRight className="w-4 h-4" />
                                    Growth trajectory alignment
                                </div>
                                <div className="text-sm flex items-center gap-2">
                                    <ArrowRight className="w-4 h-4" />
                                    IP portfolio optimization
                                </div>
                                <div className="text-sm flex items-center gap-2">
                                    <ArrowRight className="w-4 h-4" />
                                    Market protection strategy
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default Main